/**
 * Press Box → Settings. 2026-10-17.
 *
 *   • AI analysis — after each game the transcript goes to the stats AI on
 *     the GPU box (Gemma 4 31B on the 5090). Shows which model the server is
 *     pointed at and whether it answers.
 *   • Public stats page — on/off, how players are shown, the link (and a new
 *     link to kill the old one).
 *   • WhatsApp — parents' game updates, play-by-play, and "check before
 *     sending" (the existing team card, extended).
 */
import { useEffect, useRef, useState } from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import { Sparkles, Globe, Copy, RefreshCw, CheckCircle2, AlertCircle, Image as ImageIcon, Upload, Trash2, Mic, Mail, Loader2, Clock, CalendarRange, Download, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api.js';
import { useAuth } from '../../context/AuthContext.jsx';
import TeamWhatsAppCard from '../../components/TeamWhatsAppCard.jsx';
import PressBoxShell from './PressBoxShell.jsx';
import ScheduleImportCard from './ScheduleImportCard.jsx';
import { PRIVACY_LABELS, PRESS_BOX, modelAlias } from '../../constants/pressBox.js';
import { useNavigate } from 'react-router-dom';

const fileB64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result || ''));
  r.onerror = () => reject(new Error('Could not read the file'));
  r.readAsDataURL(file);
});

/** 2026-10-18 — team logo for the public page (press_box teams). */
function LogoCard({ teamId, hasLogo: hasLogo0 }) {
  const [hasLogo, setHasLogo] = useState(!!hasLogo0);
  const [imgUrl, setImgUrl] = useState(null);     // blob URL (the API wants the bearer token, so no bare <img src>)
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    let revoke = null;
    if (hasLogo) {
      api.get(`/teams/${teamId}/press-box/logo`, { responseType: 'blob' })
        .then((r) => { revoke = URL.createObjectURL(r.data); setImgUrl(revoke); })
        .catch(() => setImgUrl(null));
    } else setImgUrl(null);
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [teamId, hasLogo, busy]);   // eslint-disable-line react-hooks/exhaustive-deps
  const pick = async (file) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) return toast.error('The logo must be a PNG, JPEG or WebP image');
    if (file.size > 2 * 1024 * 1024) return toast.error('The logo must be 2 MB or smaller');
    setBusy(true);
    try {
      await api.post(`/teams/${teamId}/press-box/logo`, { data: await fileB64(file), mimeType: file.type });
      setHasLogo(true);
      toast.success('Logo saved');
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save the logo'); }
    finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/teams/${teamId}/press-box/logo`); setHasLogo(false); toast.success('Logo removed'); }
    catch { toast.error('Could not remove the logo'); } finally { setBusy(false); }
  };
  return (
    <div className="card mb-4" data-logo-settings>
      <div className="flex items-center gap-2 font-bold mb-2"><ImageIcon size={16} style={{ color: 'var(--ice)' }} /> Team logo</div>
      <div className="text-sm mb-3" style={{ color: 'var(--text-2)' }}>Shows at the top of the public stats page.</div>
      <div className="flex items-center gap-3">
        {hasLogo && imgUrl
          ? <img src={imgUrl} alt="Team logo"
              style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover', border: '1px solid var(--border)' }} />
          : <div style={{ width: 56, height: 56, borderRadius: 12, border: '1px dashed var(--border)', display: 'grid', placeItems: 'center', color: 'var(--text-3)' }}><ImageIcon size={20} /></div>}
        <button type="button" className="btn-ghost" style={{ padding: '7px 10px' }} onClick={() => ref.current?.click()} disabled={busy}>
          <Upload size={13} /> {hasLogo ? 'Change' : 'Upload'}
        </button>
        {hasLogo && <button type="button" className="btn-ghost" style={{ padding: '7px 10px', color: 'var(--penalty)' }} onClick={remove} disabled={busy}><Trash2 size={13} /> Remove</button>}
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={(e) => pick(e.target.files?.[0])} />
      </div>
    </div>
  );
}

/**
 * 2026-10-19 — who may record, and what each person may do.
 * Roles: record only / record + edit stats / full admin. The owner and any
 * coach are always admins. Inviting is the normal email invite with the
 * role attached — they get access once they accept.
 */
const ROLE_OPTIONS = [
  ['record', 'Record games only'],
  ['edit', 'Record + edit stats'],
  ['admin', 'Full admin'],
];

function RecordersCard({ teamId, userId }) {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('record');
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/teams/${teamId}/press-box/recorders`).then((r) => setData(r.data))
    .catch(() => setData({ error: true }));
  useEffect(() => { load(); }, [teamId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const invite = async () => {
    const e = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return toast.error('That doesn\u2019t look like an email address');
    setBusy(true);
    try {
      await api.post(`/teams/${teamId}/invite`, { email: e, name: e.split('@')[0],
        memberRole: inviteRole === 'admin' ? 'coach' : 'manager', pressBoxRole: inviteRole });
      setEmail(''); toast.success('Invitation sent'); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Could not send the invite'); }
    finally { setBusy(false); }
  };
  const setRole = async (uid, role) => {
    try { await api.put(`/teams/${teamId}/press-box/recorders/${uid}`, { role }); toast.success('Role changed'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Could not change the role'); }
  };
  const remove = async (uid) => {
    if (!window.confirm('Remove this recorder? They keep their account; they just can\u2019t record or upload here any more.')) return;
    try { await api.delete(`/teams/${teamId}/press-box/recorders/${uid}`); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Could not remove them'); }
  };
  return (
    <div className="card mb-4" data-recorders>
      <div className="flex items-center gap-2 font-bold mb-2"><Mic size={16} style={{ color: 'var(--gold)' }} /> Who records</div>
      <div className="text-sm mb-3" style={{ color: 'var(--text-2)' }}>
        Away for a game? Invite another parent to record it. They get an email invitation and,
        once they accept, exactly the access you pick: record only, record and fix stats, or full admin
        (roster, schedule and settings too).
      </div>
      {!data && <div className="text-sm" style={{ color: 'var(--text-3)' }}>Loading…</div>}
      {data?.error && <div className="text-sm" style={{ color: 'var(--penalty)' }}>Could not load the recorders.</div>}
      {data?.recorders && (
        <>
          {data.recorders.map((r) => (
            <div key={r.userId} className="flex items-center justify-between gap-2 text-sm mb-2" style={{ flexWrap: 'wrap' }}>
              <span style={{ minWidth: 180 }}>
                <strong>{r.name || r.email || 'Recorder'}</strong>
                {r.email && <span style={{ color: 'var(--text-3)' }}> — {r.email}</span>}
                {r.isOwner && <span style={{ color: 'var(--gold)' }}> · owner</span>}
              </span>
              <span className="flex items-center gap-1">
                {r.isOwner || r.role === 'coach' ? (
                  <span className="badge" style={{ background: 'var(--surface-2)', color: 'var(--text-2)', border: '1px solid var(--border)' }}>Full admin</span>
                ) : (
                  <select className="input-field" style={{ width: 'auto', padding: '6px 10px', fontSize: 12 }}
                    value={r.pbRole || 'record'} onChange={(e) => setRole(r.userId, e.target.value)} aria-label={`Role for ${r.name || r.email}`}>
                    {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                )}
                {!r.isOwner && r.userId !== userId && (
                  <button type="button" className="btn-ghost" style={{ padding: '6px 9px', color: 'var(--penalty)' }} onClick={() => remove(r.userId)} aria-label="Remove recorder"><Trash2 size={13} /></button>
                )}
              </span>
            </div>
          ))}
          {data.pending?.map((p) => (
            <div key={p.id} className="text-sm mb-1" style={{ color: 'var(--text-3)' }}>
              {p.email} — invited ({ROLE_OPTIONS.find(([v]) => v === p.pbRole)?.[1] || 'Record games only'}), not accepted yet
            </div>
          ))}
          <div className="flex items-center gap-2 mt-3" style={{ flexWrap: 'wrap' }}>
            <input className="input-field" style={{ flex: 1, minWidth: 180, padding: '10px 12px' }} type="email" placeholder="their@email.com"
              value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && invite()} />
            <select className="input-field" style={{ width: 'auto', padding: '10px 12px', fontSize: 13 }} value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)} aria-label="Role for the invitee">
              {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button type="button" className="btn-ice" onClick={invite} disabled={busy}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Invite
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** 2026-10-19 — clock + season phases for the schedule. */
/* 2026-10-24 (per Gilles) — the league webcal the schedule came from: shown
   with a copy button, and "Sync now" re-reads it (finals for played games,
   games the league added, opponents for TBA). After every game the server
   also reads it on its own for the final: 30 min after, then every 30 min,
   for up to 3 days. */
function ScheduleFeedCard({ teamId }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const load = () => api.get(`/teams/${teamId}/press-box/schedule/feed`).then(({ data }) => { setF(data); setLast(data.lastSync || null); })
    .catch(() => setF({ loadFailed: true }));
  useEffect(() => { load(); }, [teamId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const copy = async () => {
    try { await navigator.clipboard.writeText(f.url); toast.success('Webcal link copied'); }
    catch { toast.error('Copy it from the box'); }
  };
  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/press-box/schedule/feed/sync`, {}, { timeout: 60000 });
      setLast(data.summary);
      setF(x => ({ ...x, syncedAt: data.syncedAt, error: null }));
      const s2 = data.summary;
      toast.success(s2.scores || s2.added || s2.opponents
        ? `Synced: ${s2.scores} final score${s2.scores === 1 ? '' : 's'}, ${s2.added} new game${s2.added === 1 ? '' : 's'}${s2.opponents ? `, ${s2.opponents} opponent${s2.opponents === 1 ? '' : 's'} filled in` : ''}`
        : 'Synced — nothing new on the league calendar');
    } catch (e) { toast.error(e.response?.data?.error || 'Could not sync'); load(); }
    finally { setBusy(false); }
  };
  if (!f || f.loadFailed) return null;
  return (
    <div className="card mb-4" data-schedule-feed>
      <div className="flex items-center gap-2 font-bold mb-1"><CalendarRange size={16} style={{ color: 'var(--ice)' }} /> League calendar (webcal)</div>
      {!f.url ? (
        <div className="text-sm" style={{ color: 'var(--text-2)' }}>
          No webcal saved yet. Import the schedule from the league&rsquo;s webcal link (above) and it is kept here: Press Box then
          picks up each game&rsquo;s final score from it by itself.
        </div>
      ) : <>
        <div className="text-sm mb-2" style={{ color: 'var(--text-2)' }}>
          The schedule came from this link. After every game Press Box reads it for the final score — 30 minutes after the game,
          then every 30 minutes for up to 3 days; with still nothing, it asks you to confirm the final.
        </div>
        <div className="flex items-center gap-2 mb-2">
          <input className="input-field" readOnly value={f.url} onFocus={(e) => e.target.select()} style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} data-feed-url />
          <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} onClick={copy} title="Copy the webcal link" aria-label="Copy the webcal link"><Copy size={14} /></button>
        </div>
        <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
          <button type="button" className="btn-ice" disabled={busy} onClick={sync} data-feed-sync>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Sync now
          </button>
          <span className="text-xs" style={{ color: f.error ? 'var(--penalty)' : 'var(--text-3)' }}>
            {f.syncedAt ? `Last read ${new Date(f.syncedAt).toLocaleString()}` : 'Not read since the import'}
          </span>
        </div>
        {f.error && <div className="text-xs mt-2" style={{ color: 'var(--penalty)' }}><AlertTriangle size={11} /> {f.error}</div>}
        {last && (
          <div className="text-xs mt-2" style={{ color: 'var(--text-2)' }}>
            {last.mode === 'manual' ? 'Last sync' : 'Last automatic check'}: {last.scores} final score{last.scores === 1 ? '' : 's'} picked up
            {last.mode === 'manual' ? `, ${last.added} new game${last.added === 1 ? '' : 's'} added${last.opponents ? `, ${last.opponents} opponent${last.opponents === 1 ? '' : 's'} filled in` : ''}` : ''}.
            {Array.isArray(last.timeChanges) && last.timeChanges.length > 0 && (
              <div className="mt-1" style={{ color: 'var(--puck)' }}>
                The league calendar shows a different start time for: {last.timeChanges.map(t => `${t.date} vs ${t.opponent || 'TBA'} (${t.from} → ${t.to})`).join('; ')}.
                Change it from the game&rsquo;s menu if it moved.
              </div>
            )}
          </div>
        )}
      </>}
    </div>
  );
}

/* 2026-11-04 (per Gilles) — the OFFICIAL score link: the league's scorekeeping
   app (GameSheet) publishes a second calendar with every game's final
   (home, visitor, score, result). It is the official score — it fills a
   missing final and corrects one the league website got wrong — and it
   lists tournament games the league website never does. It carries no
   scorers or penalties: those still come from score-sheet screenshots. */
function OfficialScoreFeedCard({ teamId }) {
  const [f, setF] = useState(null);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const load = () => api.get(`/teams/${teamId}/press-box/schedule/feed`).then(({ data }) => {
    setF(data.official || {}); setUrl(data.official?.url || ''); setLast(data.official?.lastSync || null);
  }).catch(() => setF({ loadFailed: true }));
  useEffect(() => { load(); }, [teamId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/teams/${teamId}/press-box/schedule/official-feed`, { url });
      setF(x => ({ ...x, url: data.url, error: null })); setUrl(data.url || '');
      toast.success(data.url ? 'Official score link saved — press Sync now to read it' : 'Official score link removed');
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save the link'); }
    finally { setBusy(false); }
  };
  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/press-box/schedule/official-feed/sync`, {}, { timeout: 60000 });
      setLast(data.summary); setF(x => ({ ...x, syncedAt: data.syncedAt, error: null }));
      const s2 = data.summary;
      toast.success(`Official scores: ${s2.filled} filled in, ${s2.corrected} corrected, ${s2.confirmed} already right${s2.added ? `, ${s2.added} game${s2.added === 1 ? '' : 's'} added` : ''}${s2.unscored ? `, ${s2.unscored} not scored yet` : ''}`, { duration: 8000 });
    } catch (e) { toast.error(e.response?.data?.error || 'Could not sync'); load(); }
    finally { setBusy(false); }
  };
  if (!f || f.loadFailed) return null;
  return (
    <div className="card mb-4" data-official-feed>
      <div className="flex items-center gap-2 font-bold mb-1"><CalendarRange size={16} style={{ color: 'var(--goal)' }} /> Official game scores (GameSheet calendar)</div>
      <div className="text-sm mb-2" style={{ color: 'var(--text-2)' }}>
        The league&rsquo;s scorekeeping app publishes a calendar with every game&rsquo;s official final. Paste its webcal link here:
        the score it gives becomes the game&rsquo;s final (it fills a missing one and corrects one the league website got wrong — never
        a score you confirmed or a score sheet you applied), and tournament games it lists are added to the schedule. It carries the
        score only; who scored and the penalties still come from score-sheet screenshots on the game page.
      </div>
      <div className="flex items-center gap-2 mb-2">
        <input className="input-field" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="webcal://api.teams.gamesheet.app/api/public/calendar/teams/…/calendar.ics"
          style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} data-official-feed-url />
        <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} disabled={busy || (url || '') === (f.url || '')} onClick={save} data-official-feed-save>Save</button>
      </div>
      {f.url && (
        <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
          <button type="button" className="btn-ice" disabled={busy} onClick={sync} data-official-feed-sync>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Sync now
          </button>
          <span className="text-xs" style={{ color: f.error ? 'var(--penalty)' : 'var(--text-3)' }}>
            {f.syncedAt ? `Last read ${new Date(f.syncedAt).toLocaleString()}` : 'Not read yet — it is also read by itself after every game, for up to 3 days'}
          </span>
        </div>
      )}
      {f.error && <div className="text-xs mt-2" style={{ color: 'var(--penalty)' }}><AlertTriangle size={11} /> {f.error}</div>}
      {last && (
        <div className="text-xs mt-2" style={{ color: 'var(--text-2)' }}>
          {last.mode === 'manual' ? 'Last sync' : 'Last automatic check'}{last.calendar ? ` of "${last.calendar}"` : ''}: {last.filled} final{last.filled === 1 ? '' : 's'} filled in, {last.corrected} corrected, {last.confirmed} already right
          {last.added ? `, ${last.added} game${last.added === 1 ? '' : 's'} added` : ''}{last.unscored ? `, ${last.unscored} not scored yet` : ''}.
          {Array.isArray(last.unmatched) && last.unmatched.length > 0 && (
            <div className="mt-1" style={{ color: 'var(--puck)' }}>
              The official score differs from one you set by hand or applied from a sheet (left as yours): {last.unmatched.map(u => `${u.date} vs ${u.opponent || 'TBA'} — yours ${u.ours}, official ${u.official}`).join('; ')}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ScheduleDisplayCard({ teamId, timeFormat: tf0, phases: p0 }) {
  const [tf, setTf] = useState(tf0 || '12h');
  const [phases, setPhases] = useState(() => ({
    preseason: { from: '', to: '', ...(p0?.preseason || {}) },
    regular: { from: '', to: '', ...(p0?.regular || {}) },
    playoffs: { from: '', to: '', ...(p0?.playoffs || {}) },
  }));
  const [busy, setBusy] = useState(false);
  const save = async (body, ok) => {
    setBusy(true);
    try { await api.patch(`/teams/${teamId}/press-box/settings`, body); toast.success(ok); }
    catch (e) { toast.error(e.response?.data?.error || 'Could not save'); }
    finally { setBusy(false); }
  };
  const setPhase = (k, field, v) => setPhases(p => ({ ...p, [k]: { ...p[k], [field]: v } }));
  const PHASES = [['preseason', 'Pre-season'], ['regular', 'Regular season'], ['playoffs', 'Playoffs']];
  return (
    <div className="card mb-4" data-schedule-display>
      <div className="flex items-center gap-2 font-bold mb-2"><Clock size={16} style={{ color: 'var(--ice)' }} /> Schedule display</div>
      <div className="mb-3">
        <span className="label">Game times</span>
        {[['12h', '7:30 PM'], ['24h', '19:30']].map(([v, ex]) => (
          <label key={v} className="flex items-center gap-2 text-sm mb-1" style={{ cursor: 'pointer' }}>
            <input type="radio" name="pb-timefmt" checked={tf === v} disabled={busy}
              onChange={() => { setTf(v); save({ time_format: v }, `Times shown as ${ex}`); }} />
            {ex} {v === '12h' ? '(default)' : '(24-hour)'}
          </label>
        ))}
      </div>
      <div>
        <span className="label"><CalendarRange size={12} style={{ verticalAlign: '-2px' }} /> Season phases</span>
        <div className="text-xs mb-2" style={{ color: 'var(--text-3)' }}>
          Mark where the pre-season ends and the playoffs begin — the schedule shows the sections.
          League feeds don&rsquo;t carry this, so it&rsquo;s set here.
        </div>
        {PHASES.map(([k, l]) => (
          <div key={k} className="flex items-center gap-2 mb-2 text-sm" style={{ flexWrap: 'wrap' }}>
            <span style={{ minWidth: 110 }}>{l}</span>
            <input type="date" className="input-field" style={{ width: 'auto', padding: '6px 10px' }}
              value={phases[k].from || ''} onChange={(e) => setPhase(k, 'from', e.target.value)} aria-label={`${l} from`} />
            <span style={{ color: 'var(--text-3)' }}>to</span>
            <input type="date" className="input-field" style={{ width: 'auto', padding: '6px 10px' }}
              value={phases[k].to || ''} onChange={(e) => setPhase(k, 'to', e.target.value)} aria-label={`${l} to`} />
          </div>
        ))}
        <button type="button" className="btn-ghost" disabled={busy}
          onClick={() => save({ season_phases: phases }, 'Season phases saved')}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : null} Save the phases
        </button>
      </div>
    </div>
  );
}

/** 2026-10-19 — every recording as one zip, and the end of the team. */
// 2026-10-28 (per Gilles, example videos reviewed) — the team's LOOK for the
// video analysis. Jerseys change by game (blue some games, white others); the
// RED PANTS are the constant marker that tells us apart on the overhead
// camera. Free text, sent to the vision model with every video job; the
// per-game "our shirts" picker on the video card says which was worn.
function TeamLookCard({ teamId, look: look0 }) {
  const [look, setLook] = useState({ jerseys: ['blue', 'white'], pants: 'red', helmets: '', notes: '', ...(look0 || {}) });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/teams/${teamId}/press-box/settings`, { team_look: {
        jerseys: String(look.jerseys || '').split ? String(look.jerseys).split(',').map(x => x.trim()).filter(Boolean) : look.jerseys,
        pants: look.pants, helmets: look.helmets, notes: look.notes } });
      toast.success('Team look saved — the video analysis uses it');
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save'); }
    finally { setSaving(false); }
  };
  const jerseysText = Array.isArray(look.jerseys) ? look.jerseys.join(', ') : String(look.jerseys || '');
  return (
    <div className="card mb-4" data-team-look>
      <div className="font-bold mb-1">How we look on video</div>
      <div className="text-sm mb-2" style={{ color: 'var(--text-2)' }}>
        The video analysis tells the teams apart by this. List every jersey colour the team wears
        (pick the one worn on each game's video card); the pants are the reliable, never-changing marker.
      </div>
      <div className="flex items-center gap-3" style={{ flexWrap: 'wrap' }}>
        <label className="text-xs" style={{ color: 'var(--text-3)' }}>Jersey colours
          <input className="input-field" style={{ width: 140, marginLeft: 6, padding: '5px 8px' }} placeholder="blue, white"
            value={jerseysText} onChange={(e) => setLook(l => ({ ...l, jerseys: e.target.value }))} data-look-jerseys /></label>
        <label className="text-xs" style={{ color: 'var(--text-3)' }}>Pants
          <input className="input-field" style={{ width: 90, marginLeft: 6, padding: '5px 8px' }} placeholder="red"
            value={look.pants || ''} onChange={(e) => setLook(l => ({ ...l, pants: e.target.value }))} data-look-pants /></label>
        <label className="text-xs" style={{ color: 'var(--text-3)' }}>Helmets
          <input className="input-field" style={{ width: 90, marginLeft: 6, padding: '5px 8px' }} placeholder="red"
            value={look.helmets || ''} onChange={(e) => setLook(l => ({ ...l, helmets: e.target.value }))} /></label>
        <label className="text-xs" style={{ color: 'var(--text-3)' }}>Notes
          <input className="input-field" style={{ width: 220, marginLeft: 6, padding: '5px 8px' }} placeholder="e.g. white helmets for goalies"
            value={look.notes || ''} onChange={(e) => setLook(l => ({ ...l, notes: e.target.value }))} /></label>
        <button type="button" className="btn-ice" disabled={saving} onClick={save} data-look-save>Save</button>
      </div>
    </div>
  );
}

function DangerCard({ teamId, teamName }) {
  const navigate = useNavigate();
  const [confirmName, setConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [open, setOpen] = useState(false);
  const downloadAll = async () => {
    toast('Building the zip — this can take a few minutes for a season of tapes', { icon: '⬇️' });
    try {
      const res = await api.get(`/teams/${teamId}/press-box/recordings/export`, { responseType: 'blob', timeout: 1800000 });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = 'recordings.zip';
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 10000);
    } catch (e) {
      let msg = 'Could not build the zip';
      try { msg = JSON.parse(await e.response?.data?.text?.())?.error || msg; } catch { /* keep */ }
      toast.error(msg);
    }
  };
  const del = async () => {
    if (confirmName.trim() !== teamName) return toast.error('Type the team name exactly to confirm');
    setDeleting(true);
    try {
      await api.delete(`/teams/${teamId}`);
      toast.success('Team deleted');
      navigate('/dashboard');
      window.location.reload();   // the layout re-reads the team list
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not delete the team');
      setDeleting(false);
    }
  };
  return (
    <div className="card mb-6" data-danger>
      <div className="flex items-center gap-2 font-bold mb-2"><Download size={16} style={{ color: 'var(--ice)' }} /> Your recordings</div>
      <div className="text-sm mb-2" style={{ color: 'var(--text-2)' }}>
        Every game recording, decrypted and joined, in one zip — your tapes are yours.
      </div>
      <button type="button" className="btn-ghost mb-4" onClick={downloadAll}><Download size={14} /> Download all recordings (.zip)</button>

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
        <div className="flex items-center gap-2 font-bold mb-2" style={{ color: 'var(--penalty)' }}><AlertTriangle size={16} /> Delete this team</div>
        <div className="text-sm mb-2" style={{ color: 'var(--text-2)' }}>
          Removes the team, its schedule, stats, recordings and the public page — for everyone, for good.
          Download the recordings first if you want to keep them.
        </div>
        {!open ? (
          <button type="button" className="btn-danger" onClick={() => setOpen(true)}><Trash2 size={14} /> Delete {PRESS_BOX.NAME} team…</button>
        ) : (
          <div>
            <span className="label">Type the team name to confirm: <strong style={{ color: 'var(--text)' }}>{teamName}</strong></span>
            <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
              <input className="input-field" style={{ flex: 1, minWidth: 180, padding: '10px 12px' }} placeholder={teamName}
                value={confirmName} onChange={(e) => setConfirmName(e.target.value)} />
              <button type="button" className="btn-danger" onClick={del} disabled={deleting || confirmName.trim() !== teamName}>
                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Delete for good
              </button>
              <button type="button" className="btn-ghost" onClick={() => { setOpen(false); setConfirmName(''); }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PressBoxSettingsPage() {
  const { teamId } = useParams();
  const { user } = useAuth();
  const { teams = [] } = useOutletContext() || {};
  const team = teams.find(t => String(t.id) === String(teamId));
  const standalone = team?.team_type === 'press_box';
  const [s, setS] = useState(null);
  const [role, setRole] = useState(null);
  const [ov, setOv] = useState({});        // timeFormat, phases, pbRole (overview extras)
  const [ai, setAi] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let c = false;
    api.get(`/teams/${teamId}/press-box`).then(({ data }) => { if (!c) { setS(data.settings); setRole(data.role); setOv({ timeFormat: data.timeFormat, phases: data.phases, pbRole: data.pbRole }); } })
      .catch((e) => { if (!c) setS({ error: e.response?.data?.error || 'Could not load the settings' }); });
    return () => { c = true; };
  }, [teamId]);
  const checkAi = async () => {
    setChecking(true);
    try { const { data } = await api.get(`/teams/${teamId}/press-box/ai-status`, { timeout: 25000 }); setAi(data); }
    catch { setAi({ error: 'Could not reach the stats engine' }); }
    finally { setChecking(false); }
  };
  useEffect(() => { checkAi(); }, [teamId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const patch = async (body, ok) => {
    setSaving(true);
    try { const { data } = await api.patch(`/teams/${teamId}/press-box/settings`, body); setS(data.settings); if (ok) toast.success(ok); }
    catch (e) { toast.error(e.response?.data?.error || 'Could not save'); }
    finally { setSaving(false); }
  };
  const newLink = async () => {
    if (!window.confirm('Make a new link? The old one stops working for everyone who has it.')) return;
    setSaving(true);
    try { const { data } = await api.post(`/teams/${teamId}/press-box/settings/public-link`); setS(data.settings); toast.success('New link made'); }
    catch { toast.error('Could not make a new link'); } finally { setSaving(false); }
  };
  const copy = async (text) => { try { await navigator.clipboard.writeText(text); toast.success('Link copied'); } catch { toast.error('Copy it from the box'); } };

  const coach = ['coach', 'manager'].includes(role);
  // On a standalone team the admin role gates setup cards; editors see the rest.
  const admin = standalone ? ov.pbRole === 'admin' : coach;
  const box = { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 };

  return (
    <PressBoxShell teamId={teamId} userId={user?.id}>
      {!s && <div className="text-secondary text-sm p-4">Loading…</div>}
      {s?.error && <div className="card text-center p-4">{s.error}</div>}
      {s && !s.error && (
        <>
          {/* standalone Press Box teams: logo, recorders, more games */}
          {standalone && admin && <LogoCard teamId={teamId} hasLogo={!!team?.logo_ext} />}
          {standalone && admin && <RecordersCard teamId={teamId} userId={user?.id} />}
          {standalone && admin && <ScheduleImportCard teamId={teamId} title="Import more games" />}
          {(admin || ov.pbRole === 'edit') && <ScheduleFeedCard teamId={teamId} />}
          {(admin || ov.pbRole === 'edit') && <OfficialScoreFeedCard teamId={teamId} />}
          {admin && <TeamLookCard teamId={teamId} look={s.teamLook} />}
          {standalone && admin && ov.timeFormat && <ScheduleDisplayCard key={`${teamId}-${ov.timeFormat ? 'r' : ''}`} teamId={teamId} timeFormat={ov.timeFormat} phases={ov.phases} />}

          {/* AI analysis */}
          <div className="card mb-4" data-ai-settings>
            <div className="flex items-center justify-between gap-3 mb-2" style={{ flexWrap: 'wrap' }}>
              <div className="flex items-center gap-2 font-bold"><Sparkles size={16} style={{ color: 'var(--gold)' }} /> {PRESS_BOX.AI_NAME} after each game</div>
              {coach && (
                <label className="flex items-center gap-2 text-sm" style={{ cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!s.aiAnalysis} disabled={saving}
                    onChange={(e) => patch({ ai_analysis: e.target.checked }, e.target.checked ? `${PRESS_BOX.AI_NAME} on` : `${PRESS_BOX.AI_NAME} off — rules only`)} />
                  {s.aiAnalysis ? 'On' : 'Off'}
                </label>
              )}
            </div>
            <div className="text-sm mb-3" style={{ color: 'var(--text-2)' }}>
              Speech-to-text runs on the server (Parakeet, and Whisper on a borrowed GPU when one is free). With this on, the
              transcript then goes to Press Box AI (the stats model on the GPU), which reads it in 2½-minute stretches, merges repeats and recaps, fixes calls
              the rules got wrong, and must point at the words it used for every event. If its GPU is busy, the game gets the
              rules' result first and the AI check runs again by itself when the card is free. If the AI can't be reached, the
              game keeps the rules' result — "Re-run with the stats AI" on the game tries again.
            </div>
            <div style={box} className="text-sm">
              {!ai && <span style={{ color: 'var(--text-3)' }}>Checking the stats AI…</span>}
              {ai && (ai.engine === false ? <span style={{ color: 'var(--puck)' }}>Transcription is not turned on on this server.</span>
                : ai.error ? <span style={{ color: 'var(--puck)' }}>{ai.error}</span>
                : !ai.enabled ? (
                  ai.admin ? (
                    <span style={{ color: 'var(--puck)' }}>
                      {ai.notSetUp === 'no key'
                        ? <>The stats AI is waiting for Game Day's broker key: paste it in <a href="/admin" style={{ color: 'var(--ice)' }}>Super admin → Stats AI</a>.</>
                        : <>No model is set up yet: choose one in <a href="/admin" style={{ color: 'var(--ice)' }}>Super admin → Stats AI</a>.</>}
                    </span>
                  ) : <span style={{ color: 'var(--puck)' }}>The stats AI is not set up on this server yet — games are worked out by the rules.</span>
                ) : (
                  <div>
                    <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                      {ai.ping?.ok ? <CheckCircle2 size={15} style={{ color: 'var(--goal)' }} /> : <AlertCircle size={15} style={{ color: 'var(--penalty)' }} />}
                      <strong>{modelAlias(ai.endpoint?.model) || 'the model'}</strong>
                      {/* where it runs: for the super admin only (the GPU box's address is not the team's business) */}
                      {ai.admin && <span style={{ color: 'var(--text-3)' }}>via {ai.endpoint?.provider} at {ai.endpoint?.url || '?'}</span>}
                    </div>
                    {ai.ping?.busy && (
                      <div className="text-xs mt-1" style={{ color: 'var(--puck)' }}>
                        The GPU is busy right now (a training run). Games get the rules' result first; the AI check runs again by itself when the card is free.
                      </div>
                    )}
                    <div className="text-xs mt-1" style={{ color: ai.ping?.ok ? 'var(--text-2)' : 'var(--penalty)' }}>
                      {ai.ping?.ok
                        ? (Array.isArray(ai.ping.loaded)
                          ? (ai.ping.loaded.length ? `Answering — loaded now: ${ai.ping.loaded.join(', ')}` : 'Answering (not loaded right now — the first game loads it)')
                          : (ai.ping.loaded ? 'Answering — loaded and warm' : 'Answering (not loaded right now — the first game loads it)'))
                        : `Not answering: ${ai.ping?.error || 'no reply'}`}
                    </div>
                  </div>
                ))}
              <div className="mt-2"><button type="button" className="btn-ghost" style={{ padding: '5px 10px' }} onClick={checkAi} disabled={checking}><RefreshCw size={12} /> {checking ? 'Checking…' : 'Check again'}</button></div>
            </div>
          </div>

          {/* public page */}
          <div className="card mb-4" data-public-settings>
            <div className="flex items-center justify-between gap-3 mb-2" style={{ flexWrap: 'wrap' }}>
              <div className="flex items-center gap-2 font-bold"><Globe size={16} style={{ color: 'var(--ice)' }} /> Public stats page</div>
              {coach && (
                <label className="flex items-center gap-2 text-sm" style={{ cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!s.publicEnabled} disabled={saving}
                    onChange={(e) => patch({ public_enabled: e.target.checked }, e.target.checked ? 'Public page on' : 'Public page off')} />
                  {s.publicEnabled ? 'On' : 'Off'}
                </label>
              )}
            </div>
            <div className="text-sm mb-3" style={{ color: 'var(--text-2)' }}>
              A read-only page for parents and family: record, results, each game's goals and penalties, and season leaders.
              Anyone with the link can open it, so it shows players the way you pick below — never full names. Search engines are told not to list it.
            </div>
            <div className="mb-3">
              <span className="label">Show players as</span>
              {Object.entries(PRIVACY_LABELS).map(([v, l]) => (
                <label key={v} className="flex items-center gap-2 text-sm mb-1" style={{ cursor: coach ? 'pointer' : 'default' }}>
                  <input type="radio" name="pb-privacy" checked={s.publicPrivacy === v} disabled={!coach || saving}
                    onChange={() => patch({ public_privacy: v }, 'Saved')} /> {l}
                </label>
              ))}
            </div>
            {/* 2026-10-28 (per Gilles) — the goal clips + highlight reel for
                the families, per published game */}
            <label className="flex items-center gap-2 text-sm mb-3" style={{ cursor: coach ? 'pointer' : 'default' }} data-public-clips>
              <input type="checkbox" checked={!!s.publicClips} disabled={!coach || saving}
                onChange={(e) => patch({ public_clips: e.target.checked }, e.target.checked ? 'Families see the goal clips and highlight reel' : 'Clips are off the family page')} />
              Show the goal clips &amp; highlight reel on published games
            </label>
            {s.publicEnabled && s.publicUrl && (
              <div style={box}>
                <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                  <input readOnly className="input-field" style={{ flex: 1, minWidth: 220, padding: '8px 12px', fontFamily: 'var(--font-mono)', fontSize: 12 }} value={s.publicUrl} onFocus={(e) => e.target.select()} />
                  <button type="button" className="btn-ghost" style={{ padding: '7px 10px' }} onClick={() => copy(s.publicUrl)}><Copy size={13} /> Copy</button>
                  <a className="btn-ghost" style={{ padding: '7px 10px' }} href={s.publicUrl} target="_blank" rel="noreferrer">Open</a>
                  {coach && <button type="button" className="btn-ghost" style={{ padding: '7px 10px' }} onClick={newLink} disabled={saving}><RefreshCw size={13} /> New link</button>}
                </div>
              </div>
            )}
          </div>

          {/* WhatsApp */}
          {coach ? <TeamWhatsAppCard teamId={teamId} /> : (
            <div className="card mb-4 text-sm" style={{ color: 'var(--text-2)' }}>WhatsApp updates are set up by the coach.</div>
          )}

          {standalone ? (
            <div className="text-xs mb-6" style={{ color: 'var(--text-3)' }}>
              Recording itself lives on each game (the microphone button), and uploads take a finished tape.
              Players are jersey numbers on the <Link to={`/press-box/${teamId}/roster`} style={{ color: 'var(--ice)' }}>Roster</Link> —
              names stay private, the public page shows them the way you picked above.
            </div>
          ) : (
          <div className="text-xs mb-6" style={{ color: 'var(--text-3)' }}>
            Who records and uploads: the coach, and the people picked under{' '}
            <Link to={`/team/${teamId}/settings/reminders`} style={{ color: 'var(--ice)' }}>Team Settings → Live stats — who can record</Link>.
            Managers can open Press Box, fix the stats and re-run the analysis. Which stats are tracked is set there too.
            Live tracking itself is on each game (the microphone button).
          </div>
          )}
          {admin && <StatColumnsCard teamId={teamId} />}
          {admin && <TransferCard teamId={teamId} />}
          {standalone && admin && <DangerCard teamId={teamId} teamName={team?.name || ''} />}
        </>
      )}
    </PressBoxShell>
  );
}


/** 2026-10-22 (per Gilles) — hand the team to another narrator. */
function TransferCard({ teamId }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div className="card mb-4" data-transfer-card>
      <div className="font-bold text-sm mb-1">Transfer to another narrator</div>
      <div className="text-xs mb-2" style={{ color: 'var(--text-2)' }}>
        Leaving the team? Type the new narrator&rsquo;s email: they become the owner and admin (they need a free
        Game Day account first), the public page carries on, and you stay on the team until they remove you.
      </div>
      <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
        <input type="email" className="input-field" style={{ maxWidth: 280, padding: '7px 10px' }}
          placeholder="new-narrator@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <button type="button" className="btn-ghost" style={{ color: 'var(--puck)' }} disabled={busy || !email.includes('@')}
          onClick={async () => {
            if (!window.confirm(`Transfer this Press Box team to ${email}?\n\nThey become the owner and admin. You stay on the team as a coach until they remove you.`)) return;
            setBusy(true);
            try {
              await api.post(`/teams/${teamId}/press-box/transfer`, { email });
              toast.success('Transferred — they own the team now');
              setEmail('');
            } catch (e) { toast.error(e.response?.data?.error || 'Could not transfer'); }
            finally { setBusy(false); }
          }}>
          Transfer ownership…
        </button>
      </div>
    </div>
  );
}


/**
 * 2026-10-23 (per Gilles) — which stat COLUMNS show where. Two switches per
 * stat: the family page, and the internal pages. Example: GP off on the
 * family page when attendance isn't tracked accurately.
 */
function StatColumnsCard({ teamId }) {
  const COLS = [
    ['gp', 'GP — games played'], ['g', 'G — goals'], ['a', 'A — assists'], ['p', 'P — points'],
    ['sog', 'SOG — shots on goal'], ['ms', 'MS — missed shots'], ['bs', 'BS — blocked shots'],
    ['fo', 'FO — faceoffs won–lost'], ['foPct', 'FO% — faceoff win %'], ['pim', 'PIM — penalty minutes'],
    ['sv', 'SV — saves (goalie)'], ['ga', 'GA — goals against (goalie)'], ['svPct', 'SV% — save % (goalie)'],
  ];
  const [cols, setCols] = useState(null);   // { public: [hidden], private: [hidden] }
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get(`/teams/${teamId}/press-box`).then(({ data }) => { if (alive) setCols(data.statColumns || { public: [], private: [] }); })
      .catch(() => { if (alive) setCols({ public: [], private: [] }); });
    return () => { alive = false; };
  }, [teamId]);
  if (!cols) return null;
  const toggle = async (side, key) => {
    const hid = new Set(cols[side] || []);
    if (hid.has(key)) hid.delete(key); else hid.add(key);
    const next = { ...cols, [side]: [...hid] };
    setCols(next); setBusy(true);
    try { await api.patch(`/teams/${teamId}/press-box/settings`, { stat_columns: next }); }
    catch (e) { toast.error(e.response?.data?.error || 'Could not save'); }
    finally { setBusy(false); }
  };
  return (
    <div className="card mb-4" data-stat-columns>
      <div className="font-bold text-sm mb-1">Which stats show</div>
      <div className="text-xs mb-2" style={{ color: 'var(--text-2)' }}>
        Untick a column to hide it — on the family page, on your internal pages, or both.
        Hide GP, say, when attendance isn&rsquo;t tracked accurately. The numbers are still counted; they just don&rsquo;t show.
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 13, minWidth: 380 }}>
          <thead><tr>
            <th style={{ textAlign: 'left', padding: '4px 8px', fontSize: 11, color: 'var(--text-3)' }}>Stat</th>
            <th style={{ padding: '4px 8px', fontSize: 11, color: 'var(--text-3)' }}>Family page</th>
            <th style={{ padding: '4px 8px', fontSize: 11, color: 'var(--text-3)' }}>Internal</th>
          </tr></thead>
          <tbody>
            {COLS.map(([k, label]) => (
              <tr key={k} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '5px 8px' }}>{label}</td>
                {['public', 'private'].map(side => (
                  <td key={side} style={{ textAlign: 'center', padding: '5px 8px' }}>
                    <input type="checkbox" disabled={busy} checked={!(cols[side] || []).includes(k)}
                      onChange={() => toggle(side, k)} aria-label={`${label} on ${side === 'public' ? 'the family page' : 'internal pages'}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
